/*
 * Normcore analytics collector — Phase A1 (docs/normcore-analitica-marcas.md, sections 3, 5 and 6).
 * One listener set on `document` turns the CustomEvents the theme already fires into the event dictionary of section 5, adds brand and
 * item, and hands them to three sinks: memory (`NcAnalytics.buffer`, last 200, for QA and the insights mock), the console with
 * `?nc-analytics=debug`, and `Shopify.analytics.publish('nc_<name>')` ONLY when the theme setting "Enviar eventos a Shopify" is on
 * (default off) AND `Shopify.customerPrivacy.analyticsProcessingAllowed()` is true. No server call, no cookie, no identifier, no personal
 * data: props are strings (cut at 120), numbers, booleans and arrays of strings, and keys that smell of PII are dropped.
 * `?nc-analytics=off` (kept in sessionStorage as a flag, not an identifier) silences internal traffic; `?nc-analytics=on` clears it.
 * Config comes from <script type="application/json" data-nc-analytics-config> printed by layout/theme.liquid:
 * { publish, utm, utmSource, utmMedium, threshold }.
 * `outboundUrl(url, { surface, item })` adds the UTM of finding A-1 to a brand-store link (only when config.utm, http(s) only, never
 * overwriting a utm_* already present, query and hash kept). `insights(brand, period)` is the Phase A1 mock of getBrandInsights: counts
 * built only from the buffer, anything under the threshold comes back as null and is listed in `suppressed`.
 * Phase B prep (docs/normcore-analitica-fase-b.md, 3.1 and 3.2), all OFF by default and sending NOTHING:
 * `session` (config, default false): with consent AND session on, events carry `sid`, an ephemeral uuid kept in sessionStorage
 * (`nc-analytics-sid` + `nc-analytics-sid-ts`), renewed after 30 minutes without activity, never in a cookie or in persistent storage.
 * `NcAnalytics.queue` holds copies { ...event, event_id, v: 1 } and `flush()` builds { v: 1, events } (<= 50); it only sends, with
 * navigator.sendBeacon, when `endpoint` is set in the config AND consent allows. The endpoint is null in layout/theme.liquid.
 * Pure helpers are exported for node:test.
 */
(function () {
  var MAX_BUFFER = 200;
  var SCHEMA_VERSION = 1;
  var BATCH_SIZE = 50;
  var AUTO_FLUSH_AT = 10;
  var FLUSH_DELAY_MS = 3000;
  var SID_KEY = 'nc-analytics-sid';
  var SID_TS_KEY = 'nc-analytics-sid-ts';
  var SID_TTL = 30 * 60 * 1000;
  var DEFAULTS = { publish: false, utm: true, utmSource: 'normcore', utmMedium: 'referral', threshold: 10, session: false, endpoint: null };
  var RENDER_FAILURE_REASONS = ['failed', 'blocked', 'timeout', 'unknown_status', 'other'];
  // keys that would carry personal data or free text: never allowed in an event
  // gender and age are never collected: a key that names them is dropped like any other personal key
  var PII_KEY = /e-?mail|phone|tel\b|photo|avatar|image|file|query|search|address|password|token|user|display|bio|comment|^name$|^text$|gender|genero|sexo|birth|nacimiento|edad|^age$/i;
  // closed lists shared with apps/tryon/analytics/contract.js (compared by tests/normcore/analytics-dashboard-parity.test.mjs)
  var PRICE_BANDS = ['lt25', '25-50', '50-100', '100plus'];
  var TRIGGERS = ['product', 'grid', 'card', 'community', 'history', 'brand', 'home', 'generate', 'look', 'other'];
  var TRAFFIC_SOURCES = ['exploracion', 'busqueda', 'outfits', 'colecciones', 'comunidad', 'ficha', 'externo'];
  var BODY_SLOTS = ['top', 'outer', 'bottom', 'shoes', 'socks', 'head', 'eyes', 'belt', 'bag', 'other'];
  // entry point of a try-on: the nearest ancestor of the clicked control that is one of these elements (first match wins)
  var TRIGGER_TAGS = [
    ['nc-card-try-on', 'card'], ['nc-try-on-button', 'card'], ['nc-product-detail', 'product'], ['nc-community-feed', 'community'],
    ['nc-upload-community', 'community'], ['nc-look-detail', 'community'], ['nc-history', 'history'], ['nc-brand-corner-screen', 'brand'],
    ['nc-brand-corner', 'brand'], ['nc-brand-hub', 'brand'], ['nc-generate-looks', 'generate'], ['nc-look-salon', 'look'],
    ['nc-fitting-room', 'grid'], ['nc-look-sheet', 'grid'], ['nc-home', 'home']
  ];
  var EMAIL_VALUE = /\S+@\S+\.\S+/;

  function cut(s) { return String(s).slice(0, 120); }

  // only strings, numbers, booleans and arrays of strings survive
  function sanitize(props) {
    var out = {};
    if (!props || typeof props !== 'object') return out;
    Object.keys(props).forEach(function (k) {
      if (PII_KEY.test(k)) return;
      var v = props[k];
      if (typeof v === 'string') { if (!EMAIL_VALUE.test(v)) out[k] = cut(v); }
      else if (typeof v === 'number') { if (isFinite(v)) out[k] = v; }
      else if (typeof v === 'boolean') out[k] = v;
      else if (Array.isArray(v)) {
        var list = v.filter(function (x) { return typeof x === 'string' && !EMAIL_VALUE.test(x); }).map(cut);
        if (list.length === v.length) out[k] = list;
      }
    });
    return out;
  }

  function uuid() {
    var c = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') { try { return c.randomUUID(); } catch (e) { /* fall through */ } }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (ch) {
      var r = Math.floor(Math.random() * 16);
      return (ch === 'x' ? r : (r & 3) | 8).toString(16);
    });
  }

  // ephemeral session id: storage = sessionStorage-like, now() = ms clock. '' when storage fails. Renews after SID_TTL of inactivity.
  function currentSid(storage, now) {
    try {
      var t = now();
      var sid = storage.getItem(SID_KEY);
      var last = Number(storage.getItem(SID_TS_KEY));
      if (!sid || !(last > 0) || t - last > SID_TTL) { sid = uuid(); storage.setItem(SID_KEY, sid); }
      storage.setItem(SID_TS_KEY, String(t));
      return sid;
    } catch (e) { return ''; }
  }

  function clearSid(storage) {
    try { storage.removeItem(SID_KEY); storage.removeItem(SID_TS_KEY); } catch (e) { /* ignore */ }
  }

  function hasParam(query, key) { return new RegExp('(^|[?&])' + key + '=').test(query); }

  // adds utm_* to an absolute http(s) URL; the input comes back untouched when UTM is off or the URL is not http(s)
  function buildUrl(url, opts, cfg) {
    cfg = cfg || DEFAULTS;
    var raw = typeof url === 'string' ? url : '';
    if (!cfg.utm || !/^https?:\/\/[^\s]+$/i.test(raw)) return url;
    opts = opts || {};
    var hashAt = raw.indexOf('#');
    var hash = hashAt >= 0 ? raw.slice(hashAt) : '';
    var base = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
    var query = base.indexOf('?') >= 0 ? base.slice(base.indexOf('?')) : '';
    var params = [['utm_source', cfg.utmSource], ['utm_medium', cfg.utmMedium], ['utm_campaign', opts.surface], ['utm_content', opts.item]];
    params.forEach(function (p) {
      if (!p[1] || hasParam(query, p[0])) return;
      base += (base.indexOf('?') >= 0 ? (/[?&]$/.test(base) ? '' : '&') : '?') + p[0] + '=' + encodeURIComponent(String(p[1]));
    });
    return base + hash;
  }

  // price in EUR -> closed band; the raw price is never sent. Accepts a number (euros) or a formatted text ("1.299,00 €", "59,99 €").
  function priceBandOf(price) {
    var eur = null;
    if (typeof price === 'number' && isFinite(price)) eur = price;
    else if (typeof price === 'string') {
      var t = price.replace(/[^\d.,]/g, '');
      if (t.indexOf(',') >= 0) t = t.replace(/\./g, '').replace(',', '.');
      else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
      var n = parseFloat(t);
      if (isFinite(n)) eur = n;
    }
    if (eur === null || eur < 0) return '';
    return eur < 25 ? 'lt25' : eur < 50 ? '25-50' : eur < 100 ? '50-100' : '100plus';
  }

  // where a visit comes from, for the dashboard block "Origen del tráfico": the page (path) it happens on, or the path of the referrer.
  // '' = unknown (the event simply carries no source). Same-site referrer only; another host is 'externo'.
  function trafficSourceOf(path) {
    var p = String(path || '').split('?')[0].split('#')[0];
    if (p === '' || p === '/') return 'exploracion';
    if (/^\/pages\/(marcas|hub-marca|nc-hub-marca)/.test(p)) return 'exploracion';
    if (/^\/search/.test(p)) return 'busqueda';
    if (/^\/pages\/(comunidad|nc-comunidad|subir-look|nc-subir-look)/.test(p)) return 'comunidad';
    if (/^\/pages\/(generar-looks|nc-generar-looks|probador|look)/.test(p)) return 'outfits';
    if (/^\/collections/.test(p)) return 'colecciones';
    if (/^\/products\//.test(p)) return 'ficha';
    return '';
  }
  function referrerSource(referrer, host) {
    var m = /^https?:\/\/([^/?#]+)([^?#]*)/i.exec(referrer || '');
    if (!m || m[1].toLowerCase() !== String(host || '').toLowerCase()) return 'externo';
    return trafficSourceOf(m[2] || '/');
  }

  // entry point of a try-on from the chain of tag names of a clicked control (nearest first); 'other' when none is known
  function triggerOf(tagNames) {
    var names = (tagNames || []).map(function (x) { return String(x).toLowerCase(); });
    for (var i = 0; i < names.length; i++) {
      for (var j = 0; j < TRIGGER_TAGS.length; j++) if (TRIGGER_TAGS[j][0] === names[i]) return TRIGGER_TAGS[j][1];
    }
    return 'other';
  }

  function periodFilter(period, now) {
    var m = /^(\d+)d$/.exec(String(period || ''));
    if (!m) return function () { return true; };
    var from = now - parseInt(m[1], 10) * 86400000;
    return function (e) { return Date.parse(e.ts) >= from; };
  }

  // deterministic report of section 4 built only from `events`; counts under the threshold become null and are listed in `suppressed`
  function computeInsights(events, brandSlug, period, cfg, now) {
    cfg = cfg || DEFAULTS;
    var min = typeof cfg.threshold === 'number' ? cfg.threshold : DEFAULTS.threshold;
    var keep = periodFilter(period, now === undefined ? Date.now() : now);
    var mine = (events || []).filter(function (e) { return keep(e); });
    function of(name) { return mine.filter(function (e) { return e.name === name && e.brand === brandSlug; }); }
    function bump(map, key) { if (key) map[key] = (map[key] || 0) + 1; }
    var size = {};
    of('product_option_selected').forEach(function (e) { if (e.option === 'size' && e.available === false) bump(size, e.value); });
    var out = {};
    for (var i = 0; i < mine.length; i++) {
      var e = mine[i];
      if (e.name === 'outbound_clicked' && e.brand === brandSlug) bump(out, e.surface);
    }
    var paired = {};
    of('look_item_added').forEach(function (e) {
      (e.lookBrands || []).forEach(function (b) { if (b !== brandSlug) bump(paired, b); });
    });
    var renders = mine.filter(function (e) { return e.name === 'tryon_render_completed' && (e.brands || []).indexOf(brandSlug) >= 0; }).length;
    var raw = {
      impressions: of('item_impression').length,
      brandCornerViews: of('brand_corner_viewed').length,
      productViews: of('product_viewed').length,
      favorites: of('favorite_toggled').filter(function (e) { return e.value === true; }).length,
      sizeDemandUnavailable: size,
      lookAdds: of('look_item_added').length,
      renders: renders,
      outboundClicks: out,
      pairedBrands: paired
    };
    var suppressed = [];
    var result = { brand: brandSlug, period: period || 'all', threshold: min };
    Object.keys(raw).forEach(function (k) {
      var v = raw[k];
      if (typeof v === 'number') {
        if (v < min) { result[k] = null; suppressed.push(k); } else result[k] = v;
        return;
      }
      result[k] = {};
      Object.keys(v).sort().forEach(function (sub) {
        if (v[sub] < min) { result[k][sub] = null; suppressed.push(k + '.' + sub); } else result[k][sub] = v[sub];
      });
    });
    result.suppressed = suppressed;
    return result;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { priceBandOf: priceBandOf, trafficSourceOf: trafficSourceOf, referrerSource: referrerSource, triggerOf: triggerOf, PRICE_BANDS: PRICE_BANDS, TRIGGERS: TRIGGERS, TRAFFIC_SOURCES: TRAFFIC_SOURCES, BODY_SLOTS: BODY_SLOTS, sanitize: sanitize, buildUrl: buildUrl, computeInsights: computeInsights, DEFAULTS: DEFAULTS, MAX_BUFFER: MAX_BUFFER, uuid: uuid, currentSid: currentSid, SID_TTL: SID_TTL, RENDER_FAILURE_REASONS: RENDER_FAILURE_REASONS };
    return;
  }
  if (typeof window === 'undefined' || window.NcAnalytics) return;

  // ---- config, switches ----
  var cfg = Object.assign({}, DEFAULTS);
  try {
    var node = document.querySelector('script[data-nc-analytics-config]');
    var parsed = node ? JSON.parse(node.textContent) : {};
    Object.keys(DEFAULTS).forEach(function (k) { if (parsed[k] !== undefined && parsed[k] !== null && parsed[k] !== '') cfg[k] = parsed[k]; });
    cfg.threshold = Number(cfg.threshold) > 0 ? Number(cfg.threshold) : DEFAULTS.threshold;
  } catch (e) { /* keep defaults */ }

  var debug = false;
  var off = false;
  try {
    var flag = /[?&]nc-analytics=([a-z]*)/.exec(window.location.search);
    if (flag && flag[1] === 'off') window.sessionStorage.setItem('nc-analytics-off', '1');
    if (flag && flag[1] === 'on') window.sessionStorage.removeItem('nc-analytics-off');
    debug = !!flag && flag[1] === 'debug';
    off = window.sessionStorage.getItem('nc-analytics-off') === '1';
  } catch (e) { off = !!flag && flag[1] === 'off'; }

  var buffer = [];
  var queue = [];
  var flushTimer = 0;
  var lastDelivery = { state: 'idle', at: null, count: 0 };
  var viewedItems = {}; // items whose product_viewed arrived from the product page component
  var itemBrand = {};
  var lookItems = [];
  var itemSlot = {}; // item -> body slot, from the product page (NcTryonConfig.slotOf); other events of the same item reuse it
  var itemPrice = {}; // item -> price band
  var lastProduct = ''; // the garment of the product page (gallery events carry no product id)
  var galleryDone = {};
  var lastShare = { look: '', at: 0 };

  function consent() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    try { return !!(p && p.analyticsProcessingAllowed && p.analyticsProcessingAllowed()); } catch (e) { return false; }
  }

  function pageType() {
    var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return (m && m.page && m.page.pageType) || '';
  }

  function track(name, props) {
    if (off || !name) return null;
    var event = Object.assign(sanitize(props), { name: String(name), ts: new Date().toISOString(), page: pageType() });
    if (cfg.session) {
      var sid = '';
      try { sid = consent() ? currentSid(window.sessionStorage, function () { return Date.now(); }) : ''; } catch (e) { sid = ''; }
      if (sid) event.sid = sid; else clearSid(window.sessionStorage);
    }
    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer.shift();
    queue.push(Object.assign({}, event, { event_id: uuid(), v: SCHEMA_VERSION }));
    if (queue.length > MAX_BUFFER) queue.shift();
    if (cfg.endpoint && queue.length >= AUTO_FLUSH_AT) flush();
    else scheduleFlush();
    if (debug && window.console) console.info('[nc-analytics]', event);
    if (cfg.publish && consent() && window.Shopify.analytics && window.Shopify.analytics.publish) {
      try { window.Shopify.analytics.publish('nc_' + event.name, event); } catch (e) { /* analytics never breaks the page */ }
    }
    return event;
  }

  function outboundUrl(url, opts) { return buildUrl(url, opts, cfg); }

  function insights(brandSlug, period) {
    return Promise.resolve(computeInsights(buffer, String(brandSlug || ''), period, cfg));
  }

  // builds the exact payload the contract validates ({ v: 1, events: [<= 50] }); sends ONLY with config.endpoint + consent + sendBeacon.
  // Without an endpoint (the default) it sends nothing and leaves the queue as is. Returns the payload, or null when there is nothing to send.
  // A short, consent-aware timer so a batch does not wait for the page to hide: it is sent 3 s after the first queued event, and while
  // Shopify has not reported permission it keeps the queue in memory and retries every 10 s (consent may be accepted later on this page).
  function scheduleFlush() {
    if (!cfg.endpoint || !queue.length || flushTimer) return;
    if (!consent()) lastDelivery = { state: 'consent_required', at: new Date().toISOString(), count: queue.length };
    flushTimer = setTimeout(function () {
      flushTimer = 0;
      flush();
      if (queue.length) scheduleFlush();
    }, consent() ? FLUSH_DELAY_MS : 10000);
  }

  function flush() {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = 0; }
    if (!queue.length) return null;
    var batch = queue.slice(0, BATCH_SIZE);
    var payload = { v: SCHEMA_VERSION, events: batch };
    if (!cfg.endpoint) { lastDelivery = { state: 'endpoint_missing', at: new Date().toISOString(), count: batch.length }; return payload; }
    if (!consent()) { lastDelivery = { state: 'consent_required', at: new Date().toISOString(), count: batch.length }; return payload; }
    try {
      var nav = window.navigator;
      var body = typeof Blob === 'function' ? new Blob([JSON.stringify(payload)], { type: 'application/json' }) : JSON.stringify(payload);
      if (nav && typeof nav.sendBeacon === 'function' && nav.sendBeacon(String(cfg.endpoint), body)) {
        queue.splice(0, batch.length);
        lastDelivery = { state: 'queued', at: new Date().toISOString(), count: batch.length };
      } else lastDelivery = { state: 'beacon_rejected', at: new Date().toISOString(), count: batch.length };
    } catch (e) { lastDelivery = { state: 'send_failed', at: new Date().toISOString(), count: batch.length }; /* analytics never breaks the page */ }
    return payload;
  }

  window.NcAnalytics = { track: track, buffer: buffer, queue: queue, flush: flush, outboundUrl: outboundUrl, insights: insights, config: cfg, status: function () { return { consent: consent(), queued: queue.length, lastDelivery: lastDelivery }; } };
  if (off) return;
  // Shopify.customerPrivacy only exists once the consent-tracking-api feature is loaded; the privacy banner loads it only while
  // it is showing, so without this a visitor who already answered the banner always reads as "no consent" and nothing is sent.
  if (cfg.endpoint && window.Shopify && typeof window.Shopify.loadFeatures === 'function' && !window.Shopify.customerPrivacy) {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { /* consent() reads the API on demand */ }); } catch (e) { /* analytics never breaks the page */ }
  }
  document.addEventListener('visibilitychange', function () { if (cfg.endpoint && document.visibilityState === 'hidden') flush(); });

  // ---- brand / item resolution ----
  function screenOf(el) { return el && el.closest ? el.closest('nc-brand-corner-screen') : null; }

  function brandFor(el, item) {
    var own = el && el.closest ? el.closest('[data-nc-brand]') : null;
    if (own && own.getAttribute('data-nc-brand')) return own.getAttribute('data-nc-brand');
    var screen = screenOf(el);
    if (screen && screen.slug) return screen.slug;
    return (item && itemBrand[item]) || '';
  }

  function remember(item, brand) { if (item && brand) itemBrand[item] = brand; }

  // closed list shared with apps/tryon/analytics/contract.js (RENDER_FAILURE_REASONS, compared by the test); anything else is 'failed'
  function renderFailureReason(d) {
    if (d.status === 'blocked') return 'blocked';
    return RENDER_FAILURE_REASONS.indexOf(d.reason) >= 0 ? d.reason : 'failed';
  }

  function lookBrands() {
    var seen = [];
    lookItems.forEach(function (id) { var b = itemBrand[id]; if (b && seen.indexOf(b) < 0) seen.push(b); });
    return seen;
  }

  // body slots with a garment in the current look (metric M25): only those the session has seen (product page, cards), no repeats
  function lookSlots() {
    var seen = [];
    lookItems.forEach(function (id) { var sl = itemSlot[id]; if (sl && seen.indexOf(sl) < 0) seen.push(sl); });
    return seen;
  }

  // brands of a look card, when the page prints them (data-nc-brands="a,b"); otherwise the brands of the current look is NOT a guess for a liked look: empty
  function lookBrandsOf(el) {
    var host = el && el.closest ? el.closest('[data-nc-brands]') : null;
    var raw = host ? host.getAttribute('data-nc-brands') : '';
    return raw ? raw.split(',').map(function (x) { return x.trim(); }).filter(Boolean).slice(0, 20) : [];
  }

  function cardItem(card) {
    var t = card.querySelector('.nc-toggle[data-id], .nc-card__link[data-id]');
    var ids = [card.getAttribute('data-nc-item'), card.getAttribute('data-id'), t && t.getAttribute('data-id')];
    for (var i = 0; i < ids.length; i++) if (ids[i] && ids[i].indexOf('__') < 0) return ids[i];
    return '';
  }

  function surfaceOf(card) {
    if (screenOf(card)) return 'brand-corner';
    if (card.closest('nc-product-detail')) return 'ficha-relacionados';
    return pageType() || 'other';
  }

  function listen(type, fn) { document.addEventListener(type, function (e) { try { fn(e, e.detail || {}); } catch (err) { /* ignore */ } }); }

  // ---- event map (section 5) ----
  listen('nc-brand-corner:viewed', function (e, d) { track('brand_corner_viewed', { brand: d.brand, source: d.source }); });
  listen('nc-tabs:change', function (e, d) {
    var screen = screenOf(e.target);
    if (screen) track('brand_corner_tab_changed', { brand: screen.slug, tab: d.id });
  });
  listen('nc-brand-corner:open', function (e, d) { track('brand_corner_item_opened', { brand: d.brand || brandFor(e.target), kind: d.kind, id: d.id }); });
  function slotValue(x) { return BODY_SLOTS.indexOf(x) >= 0 ? x : ''; }
  listen('nc-product:viewed', function (e, d) {
    if (d.id) viewedItems[d.id] = true;
    remember(d.id, d.brand);
    lastProduct = d.id || '';
    var slot = slotValue(d.slot);
    if (d.id && slot) itemSlot[d.id] = slot;
    var band = priceBandOf(d.price);
    if (d.id && band) itemPrice[d.id] = band;
    var props = { item: d.id, brand: d.brand, source: referrerSource(document.referrer, window.location.host) };
    if (band) props.price_band = band;
    if (typeof d.onSale === 'boolean') props.on_sale = d.onSale; // only when the record says so: never inferred
    if (slot) props.slot = slot;
    track('product_viewed', props);
  });
  // The standard product template prints its item (layout/theme.liquid, only on template 'product') so a view is counted even when the
  // product component never announces one. It waits 2 s for the component, whose event is richer (price band, slot, origin), and
  // fires only if that event did not arrive: one product_viewed per garment and page.
  (function () {
    var ctxNode = document.querySelector('script[data-nc-product-context]');
    if (!ctxNode) return;
    var ctxItem = '';
    try { ctxItem = String((JSON.parse(ctxNode.textContent || '{}') || {}).item || ''); } catch (err) { ctxItem = ''; }
    if (!ctxItem) return;
    setTimeout(function () {
      if (viewedItems[ctxItem]) return;
      viewedItems[ctxItem] = true;
      lastProduct = ctxItem;
      track('product_viewed', { item: ctxItem, brand: itemBrand[ctxItem] || '', source: referrerSource(document.referrer, window.location.host) });
    }, 2000);
  })();
  // the gallery of the product page: once per garment and page (a second photo was opened)
  listen('nc-gallery:change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('nc-product-detail') || !lastProduct || galleryDone[lastProduct]) return;
    galleryDone[lastProduct] = true;
    track('product_gallery_viewed', { item: lastProduct, brand: itemBrand[lastProduct] || '' });
  });
  listen('nc-product:option', function (e, d) {
    var item = d.productId;
    track('product_option_selected', { item: item, brand: d.brand || brandFor(e.target, item), option: d.name, value: d.value, available: d.available });
  });
  listen('nc-card:toggle', function (e, d) {
    var card = e.target.closest && e.target.closest('.nc-product-card');
    if (d.kind === 'favorite') {
      var item = d.id;
      track('favorite_toggled', { item: item, brand: brandFor(card || e.target, item), value: !!d.pressed });
    } else if (d.kind === 'like') track('look_liked', { look: d.id, brands: lookBrandsOf(card || e.target), value: !!d.pressed });
    else if (d.kind === 'save') track('look_saved', { look: d.id, value: !!d.pressed });
  });
  listen('nc-fitting:added', function (e, d) {
    if (d.status !== 'added') return;
    if (lookItems.indexOf(d.productId) < 0) lookItems.push(d.productId);
    var added = { item: d.productId, brand: itemBrand[d.productId], lookBrands: lookBrands() };
    if (itemSlot[d.productId]) added.slot = itemSlot[d.productId];
    track('look_item_added', added);
  });
  listen('nc-look:change', function (e, d) {
    if (d.action === 'removed') lookItems = lookItems.filter(function (id) { return id !== d.productId; });
    else if (d.action === 'restored' && lookItems.indexOf(d.productId) < 0) lookItems.push(d.productId);
  });
  // tryon_started from EVERY entry point. All of them (grid "Probar", card quick try-on, community, history, product page, brand pages) go
  // through NcFittingRoom.add / addMany, which fires nc-fitting:added once per garment; the control that was pressed is remembered at click
  // time (capture phase) and read when the garments arrive. One event per press: the garments that arrive within 400 ms are one start.
  var pendingTrigger = { name: '', at: 0 };
  var startBatch = null;
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('button, a, [role="button"]') : null;
    if (!el) return;
    var chain = [];
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) chain.push(n.localName || '');
    pendingTrigger = { name: triggerOf(chain), at: Date.now() };
  }, true);
  listen('nc-product:tryon', function () { pendingTrigger = { name: 'product', at: Date.now() }; });
  listen('nc-fitting:added', function (e, d) {
    if (d.status !== 'added' && d.status !== 'replaced' && d.status !== 'exists') return;
    if (!startBatch) {
      var fresh = Date.now() - pendingTrigger.at < 5000;
      startBatch = { items: [], trigger: fresh ? pendingTrigger.name : 'other' };
      pendingTrigger = { name: '', at: 0 };
      setTimeout(function () {
        var batch = startBatch; startBatch = null;
        var brands = [];
        batch.items.forEach(function (id) { var b = itemBrand[id]; if (b && brands.indexOf(b) < 0) brands.push(b); });
        track('tryon_started', { items: batch.items, brands: brands, trigger: TRIGGERS.indexOf(batch.trigger) >= 0 ? batch.trigger : 'other' });
      }, 400);
    }
    if (d.productId && startBatch.items.indexOf(d.productId) < 0 && startBatch.items.length < 20) startBatch.items.push(d.productId);
  });
  listen('nc-fitting:render', function (e, d) {
    if (d.status === 'ready') {
      var done = { items: lookItems.slice(), brands: lookBrands() };
      var slots = lookSlots();
      if (slots.length) done.slots = slots;
      track('tryon_render_completed', done);
    }
    else if (d.status === 'failed' || d.status === 'blocked') track('tryon_render_failed', { brands: lookBrands(), items: lookItems.slice(), reason: renderFailureReason(d) });
  });
  // a look was shared: the fitting room fires nc-fitting:share and the generator relays it as nc-generate-looks:shared; one event per look in 1 s
  function noteShare(e, d) {
    var look = d.lookId ? String(d.lookId) : '';
    var t = Date.now();
    if (lastShare.look === look && t - lastShare.at < 1000) return;
    lastShare = { look: look, at: t };
    track('look_shared', { look: look, brands: lookBrands(), ai: !!d.aiGenerated });
  }
  listen('nc-fitting:share', noteShare);
  listen('nc-generate-looks:shared', noteShare);
  listen('nc-fitting:save', function (e, d) { track('look_saved', { look: d.lookId, brands: lookBrands() }); });
  listen('nc-look:save', function () { track('look_saved', { brands: lookBrands() }); });
  listen('nc-upload-look:published', function (e, d) { track('look_published', { look: d.lookId, brands: lookBrands() }); });

  // outbound clicks to a brand store
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-nc-outbound]');
    if (!a) return;
    var item = a.getAttribute('data-nc-item') || '';
    var out = { item: item, brand: a.getAttribute('data-nc-brand') || itemBrand[item] || '', surface: a.getAttribute('data-nc-surface') || '', host: a.hostname || '' };
    if (itemSlot[item]) out.slot = itemSlot[item];
    track('outbound_clicked', out);
  });

  // ---- item_impression: 50 % of the card for 1 s, once per item and page ----
  var seen = {};
  var timers = typeof WeakMap === 'function' ? new WeakMap() : null;
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var io = null;

  function dwell(card) {
    var item = cardItem(card);
    if (!item || seen[item]) return;
    seen[item] = true;
    var brand = brandFor(card, item);
    remember(item, brand);
    var surface = surfaceOf(card);
    var props = { item: item, brand: brand, surface: surface };
    var band = priceBandOf((card.querySelector('.nc-product-card__price') || {}).textContent) || itemPrice[item];
    if (band) props.price_band = band;
    var sale = card.getAttribute('data-nc-on-sale'); // printed by a page that knows it; never inferred from the text
    if (sale === 'true' || sale === 'false') props.on_sale = sale === 'true';
    var src = surface === 'ficha-relacionados' ? 'ficha' : surface === 'brand-corner' ? '' : trafficSourceOf(window.location.pathname);
    if (src) props.source = src;
    var slot = slotValue(card.getAttribute('data-nc-slot')) || itemSlot[item];
    if (slot) props.slot = slot;
    track('item_impression', props);
    io.unobserve(card);
  }

  function observe(card) {
    if (!io || watched.has(card)) return;
    watched.add(card);
    io.observe(card);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.classList.contains('nc-product-card')) observe(root);
    if (root.firstElementChild) Array.prototype.forEach.call(root.querySelectorAll('.nc-product-card'), observe);
  }

  if (typeof IntersectionObserver === 'function' && timers && watched) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var t = timers.get(en.target);
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (!t) timers.set(en.target, setTimeout(function () { timers.delete(en.target); dwell(en.target); }, 1000));
        } else if (t) { clearTimeout(t); timers.delete(en.target); }
      });
    }, { threshold: [0.5] });
    scan(document.body);
    new MutationObserver(function (records) {
      records.forEach(function (r) { Array.prototype.forEach.call(r.addedNodes, scan); });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
